import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { theme } from '../theme';

const styles = {
  container: { maxWidth: '760px', margin: '0 auto', padding: '32px 24px' },
  title: { fontSize: '24px', fontWeight: 700, color: theme.textPrimary, marginBottom: '8px' },
  intro: { color: theme.textSecondary, fontSize: '14px', lineHeight: 1.6 },
  toc: { color: theme.textSecondary, fontSize: '14px', lineHeight: 1.8, paddingLeft: '20px' },
  section: {
    background: theme.bgPanel,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.radiusLg,
    padding: '16px 20px',
    marginTop: '16px',
  },
  heading: { fontSize: '17px', fontWeight: 600, color: theme.textPrimary, margin: '0 0 8px' },
  subheading: { fontSize: '15px', fontWeight: 600, color: theme.textPrimary, margin: '16px 0 6px' },
  body: { color: theme.textSecondary, fontSize: '14px', lineHeight: 1.6 },
  list: { color: theme.textSecondary, fontSize: '14px', lineHeight: 1.6, paddingLeft: '20px' },
  code: {
    background: theme.bgInput,
    color: theme.textPrimary,
    borderRadius: theme.radiusMd,
    padding: '10px 12px',
    fontSize: '13px',
    overflowX: 'auto' as const,
    whiteSpace: 'pre' as const,
  },
  link: { color: theme.accent },
};

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} style={styles.section}>
      <h2 style={styles.heading}>{title}</h2>
      <div style={styles.body}>{children}</div>
    </section>
  );
}

const dialogue = `func rusty():
    say("Rusty", "Hey, haven't seen you before.")
    say("Rusty", "This village is pretty quiet most days.")`;

const questDialogue = `func rusty():
    if is_quest_active(quest.Find_My_Cat):
        say("Rusty", "Still looking for my cat?")
    else:
        start_quest(quest.Find_My_Cat)
        say("Rusty", "Can you help me find my cat?")`;

export function Guide() {
  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Your first game on Lune</h1>
      <p style={styles.intro}>
        This guide takes you from your invite to a game you can share with friends. You need a
        browser and nothing else. Plan on about an hour. If a button or label here does not match
        what you see, that is our mistake, not yours: tell us with <strong>Report a problem</strong>
        .
      </p>
      <ol style={styles.toc}>
        <li>
          <a href="#signup" style={styles.link}>
            Sign up with your invite
          </a>
        </li>
        <li>
          <a href="#first-game" style={styles.link}>
            Make your first game
          </a>
        </li>
        <li>
          <a href="#play" style={styles.link}>
            Play it
          </a>
        </li>
        <li>
          <a href="#build" style={styles.link}>
            Add a character who talks
          </a>
        </li>
        <li>
          <a href="#quest" style={styles.link}>
            Give them a quest (optional)
          </a>
        </li>
        <li>
          <a href="#share" style={styles.link}>
            Share your game
          </a>
        </li>
        <li>
          <a href="#limits" style={styles.link}>
            What the free plan includes
          </a>
        </li>
        <li>
          <a href="#safety" style={styles.link}>
            Your work is safe
          </a>
        </li>
        <li>
          <a href="#help" style={styles.link}>
            Getting help
          </a>
        </li>
      </ol>

      <Section id="signup" title="1. Sign up with your invite">
        <ol style={styles.list}>
          <li>
            Open the link in your invite email. It takes you to the sign-up page with your{' '}
            <strong>Invite code</strong> already filled in.
          </li>
          <li>
            Fill in <strong>Email</strong>, <strong>Username</strong>, <strong>Password</strong> and{' '}
            <strong>Confirm password</strong>.
          </li>
          <li>
            Tick <strong>I am 18 or older</strong> and <strong>I agree to the beta terms</strong>,
            then click <strong>Create Account</strong>.
          </li>
          <li>
            You land on your dashboard. A banner says{' '}
            <strong>Verify your email to create games</strong>. Open the email we just sent you and
            click the link in it. No email after a few minutes? Check your spam folder, or click{' '}
            <strong>Resend email</strong> on the banner.
          </li>
        </ol>
      </Section>

      <Section id="first-game" title="2. Make your first game">
        <ol style={styles.list}>
          <li>
            On the dashboard, click <strong>+ New Project</strong>.
          </li>
          <li>
            Type a <strong>Project Name</strong>. The <strong>Slug</strong> fills itself in; it
            becomes part of your game&apos;s link, so keep it short.
          </li>
          <li>
            Set <strong>Starter Template</strong> to <strong>Starter Village</strong>. It gives you
            a village with an Elder and a shop, and a field of Slimes next door, so you have
            something to play right away. (<strong>Blank</strong> is a single empty map.)
          </li>
          <li>
            Click <strong>Create Project</strong>. Your game appears as a card on the dashboard.
          </li>
          <li>
            Click <strong>Start</strong> on the card and give it a few seconds. When it is running,
            the card shows <strong>Play</strong> and <strong>Editor</strong>.
          </li>
        </ol>
      </Section>

      <Section id="play" title="3. Play it">
        <ol style={styles.list}>
          <li>
            Click <strong>Play</strong>. The game opens in a new tab.
          </li>
          <li>
            Click <strong>New Account</strong>. This is a login for your game only, separate from
            your Lune account. Pick any username and password.
          </li>
          <li>
            Make a character and click <strong>Create Character</strong>.
          </li>
          <li>
            Walk around with the arrow keys or WASD. Keep this tab open; you will come back to it.
          </li>
        </ol>
      </Section>

      <Section id="build" title="4. Add a character who talks">
        <p>
          Now you build something. Go back to the dashboard and click <strong>Editor</strong>. It
          opens in a new tab and signs you in with your Lune account. Every editor below is in the{' '}
          <strong>Editors</strong> menu at the top.
        </p>

        <h3 style={styles.subheading}>Write what they say</h3>
        <ol style={styles.list}>
          <li>
            Open <strong>Documents</strong>. Type a short name for your character (for example{' '}
            <code>rusty</code>) in the box at the top left and click <strong>New</strong>. The
            document opens with a sample conversation already named after it:{' '}
            <code>func rusty():</code>.
          </li>
          <li>
            Replace the sample with your own lines, like this:
            <pre style={styles.code}>{dialogue}</pre>
          </li>
          <li>
            Each <code>say(&quot;Name&quot;, &quot;Text&quot;)</code> line is one speech bubble: who
            is talking, then what they say. Lines that belong to the conversation are indented under
            the <code>func</code> line. Press Enter after a line that ends in a colon and the editor
            indents the next line for you.
          </li>
          <li>
            Save with Ctrl+S (Cmd+S on a Mac). The bar at the bottom says{' '}
            <strong>No problems.</strong> when the document is right, or names the line to fix.
          </li>
        </ol>

        <h3 style={styles.subheading}>Make the character</h3>
        <ol style={styles.list}>
          <li>
            Open the <strong>NPC Editor</strong> and click <strong>New NPC</strong>.
          </li>
          <li>
            Click <strong>New NPC</strong> at the top of the form and type their name. Leave the
            list next to it on <strong>Friendly</strong> (other choices attack the player on sight).
          </li>
          <li>
            On the right, pick a look from the <strong>Sprite Set</strong> list.
          </li>
          <li>
            On the <strong>Conversation</strong> tab, pick your conversation (for example{' '}
            <code>rusty</code>) from the <strong>Conversation</strong> list.
          </li>
          <li>
            Click <strong>Save NPC</strong>.
          </li>
        </ol>

        <h3 style={styles.subheading}>Put them in the village</h3>
        <ol style={styles.list}>
          <li>
            Click the <strong>Map</strong> tab, then <strong>Village</strong> in the{' '}
            <strong>Maps</strong> list on the left.
          </li>
          <li>
            In the column of icons on the right, click <strong>NPC Spawns</strong>. Click your
            character in the list, then click an open spot on the map.
          </li>
          <li>Save the map with Ctrl+S.</li>
        </ol>

        <h3 style={styles.subheading}>Meet them</h3>
        <ol style={styles.list}>
          <li>
            Go back to the game tab. Your character is already standing where you put them. Walk up
            to them, face them, and press Ctrl. Your lines show up.
          </li>
        </ol>
        <p>
          Nothing happened, or it looks wrong? Tell us which step and what you saw with{' '}
          <strong>Report a problem</strong>. &quot;Nothing happened&quot; is a useful report.
        </p>
      </Section>

      <Section id="quest" title="5. Give them a quest (optional)">
        <ol style={styles.list}>
          <li>
            Quests are an add-on you turn on once. Open <strong>Plugin Manager</strong>, type{' '}
            <code>quest</code> in the search box, tick <strong>Quests</strong>, and click{' '}
            <strong>Save</strong>.
          </li>
          <li>
            Open the <strong>Quest Editor</strong> (now in the <strong>Editors</strong> menu) and
            click <strong>New Quest</strong>.
          </li>
          <li>
            Click <strong>New Quest</strong> at the top of the form and type a name (for example
            &quot;Find My Cat&quot;). Add a <strong>Description</strong>.
          </li>
          <li>
            On the <strong>Rewards</strong> tab, click <strong>+ Add Reward</strong> and pick{' '}
            <strong>Gold</strong>. You do not need any objectives: your conversation starts the
            quest.
          </li>
          <li>
            Click <strong>Save Quest</strong>.
          </li>
          <li>
            Go back to your document and change it to hand out the quest. Use your quest&apos;s name
            with spaces turned into underscores. To go back out one indent (for <code>else:</code>),
            press Backspace.
            <pre style={styles.code}>{questDialogue}</pre>
          </li>
          <li>
            Save. Talk to your character twice. The second time they say something different,
            because the quest has started. Press Q to see it in your Quest Log.
          </li>
        </ol>
      </Section>

      <Section id="share" title="6. Share your game">
        <ol style={styles.list}>
          <li>
            Copy the address of your game tab (it ends in <code>/play/</code> and your slug) and
            send it to a friend. They do not need a Lune account.
          </li>
          <li>
            They click <strong>New Account</strong> to make their own login for your game, then make
            a character, just like you did.
          </li>
          <li>
            Your game goes to sleep after 30 minutes with nobody in it. If your friend cannot get
            in, click <strong>Start</strong> on your dashboard again.
          </li>
        </ol>
      </Section>

      <Section id="limits" title="7. What the free plan includes">
        <ul style={styles.list}>
          <li>1 game</li>
          <li>150 MB of storage for it</li>
          <li>Up to 10 players at once</li>
          <li>1 running game at a time</li>
          <li>
            Your game sleeps after 30 minutes with nobody in it, and starts again from your
            dashboard
          </li>
        </ul>
        <p>Your dashboard shows how much of each you are using, under My Projects.</p>
      </Section>

      <Section id="safety" title="8. Your work is safe">
        <p>
          Click <strong>Settings</strong> on your game&apos;s card to find these:
        </p>
        <ul style={styles.list}>
          <li>
            <strong>Restore points</strong>: one is saved every day your game changes, and before
            every restore. You can go back to any of them.
          </li>
          <li>
            <strong>Save a named version</strong> before a big change, so you can always get back to
            it.
          </li>
          <li>
            <strong>Download as zip</strong> gives you the whole game to keep.
          </li>
          <li>
            <strong>Move to Trash</strong> keeps a game for 30 days. Restore it from the Trash on
            your dashboard.
          </li>
        </ul>
        <p>
          Your <strong>Account</strong> page also has <strong>Download my data</strong>.
        </p>
      </Section>

      <Section id="help" title="9. Getting help">
        <ul style={styles.list}>
          <li>
            <strong>Report a problem</strong> is at the top of the dashboard and in the
            editor&apos;s menu bar. Bugs and ideas both go straight to us.
          </li>
          <li>
            The{' '}
            <Link to="/known-issues" style={styles.link}>
              Known issues
            </Link>{' '}
            page lists problems we already know about.
          </li>
        </ul>
      </Section>
    </div>
  );
}
